import { GUDHUB_SETTINGS } from '../../../../gudhub_settings.js';

export const attachmentsMixin = {

    //********************* LOAD PDF.JS ********************************//
    // Lazy-loads PDF.js from CDN the first time it is needed. Uses new Function
    // to prevent webpack from statically bundling the external URL.
    async _loadPdfJs() {
        if(this._pdfjsLib) return this._pdfjsLib;
        const dynamicImport = new Function('url', 'return import(url)');
        const pdfjs = await dynamicImport('https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.mjs');
        pdfjs.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.mjs';
        this._pdfjsLib = pdfjs;
        return pdfjs;
    },

    //********************* GENERATE PDF THUMBNAIL ********************************//
    // Renders the first page of a PDF File to a JPEG Blob (≤ 400 px wide)
    // using PDF.js. Returns null on failure.
    // * file (File) — the PDF file
    async generatePdfThumbnail(file) {
        const pdfjs = await this._loadPdfJs();
        const buffer = await file.arrayBuffer();
        const pdf = await pdfjs.getDocument({ data: new Uint8Array(buffer) }).promise;
        const page = await pdf.getPage(1);

        const naturalViewport = page.getViewport({ scale: 1 });
        const scale = Math.min(400 / naturalViewport.width, 600 / naturalViewport.height);
        const viewport = page.getViewport({ scale });

        const canvas = document.createElement('canvas');
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        await page.render({ canvasContext: canvas.getContext('2d'), viewport }).promise;

        return new Promise(resolve => canvas.toBlob(blob => resolve(blob), 'image/jpeg', 0.85));
    },

    //********************* UPLOAD FILE TO SERVER ********************************//
    // Uploads a file (and optional thumbnail blob) to the conversation server's
    // file-manager endpoint. Returns { filename, original_name, mime_type, size,
    // file_url, thumbnail_url }. URLs are relative — prefix node_server_url.
    // * file (File) — the file to upload
    // * thumbnailBlob (Blob|null) — optional pre-rendered thumbnail
    async uploadFileToServer(file, thumbnailBlob = null) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('user_id', this.activeUserId);
        formData.append('conversation_id', this.conversationId);
        if(thumbnailBlob) {
            formData.append('thumbnail', thumbnailBlob, 'thumbnail.jpg');
        }

        const res = await fetch(`${GUDHUB_SETTINGS.conversation_server_url}/gudhub-messenger/files/upload`, {
            method: 'POST',
            body: formData
        });

        return res.json();
    },

    //********************* ADD PENDING ATTACHMENT ********************************//
    // Stages a file for sending. For PDFs, generates a client-side thumbnail via
    // PDF.js before uploading so the server can store it without needing a PDF
    // renderer. Stores the upload Promise so sendMessage can await it.
    // * file (File) — the file to stage
    addPendingAttachment(file) {
        const id = crypto.randomUUID();
        const attachment = { id, file, status: 'uploading', serverFile: null, uploadPromise: null, localThumbnailUrl: null };

        const uploadPromise = (async () => {
            let thumbnailBlob = null;
            if(file.type === 'application/pdf') {
                thumbnailBlob = await this.generatePdfThumbnail(file).catch(() => null);
                if(thumbnailBlob) {
                    attachment.localThumbnailUrl = URL.createObjectURL(thumbnailBlob);
                    this.renderAttachmentCards();
                }
            }
            return this.uploadFileToServer(file, thumbnailBlob);
        })();

        attachment.uploadPromise = uploadPromise;
        this.pendingAttachments.push(attachment);
        this.renderAttachmentCards();

        uploadPromise
            .then(serverFile => {
                if(attachment.localThumbnailUrl) {
                    URL.revokeObjectURL(attachment.localThumbnailUrl);
                    attachment.localThumbnailUrl = null;
                }
                if(serverFile.error || !serverFile.file_url) {
                    attachment.status = 'error';
                } else {
                    attachment.serverFile = serverFile;
                    attachment.status = 'ready';
                }
                this.renderAttachmentCards();
            })
            .catch(() => {
                if(attachment.localThumbnailUrl) {
                    URL.revokeObjectURL(attachment.localThumbnailUrl);
                    attachment.localThumbnailUrl = null;
                }
                attachment.status = 'error';
                this.renderAttachmentCards();
            });
    },

    //********************* HANDLE FILE INPUT CHANGE ********************************//
    // Called by GhHtmlElement's event-rewriting when the file <input> changes.
    // * input (HTMLInputElement) — the file input element
    handleFileInputChange(input) {
        for(const file of Array.from(input.files)) {
            this.addPendingAttachment(file);
        }
        input.value = '';
    },

    //********************* RENDER ATTACHMENT CARDS ********************************//
    // Updates the .attachment_cards element with the current pendingAttachments.
    // Shows a spinner while uploading, a thumbnail (or file icon) once done,
    // and a × button to remove the attachment before sending.
    renderAttachmentCards() {
        const container = this.querySelector('.attachment_cards');
        if(!container) return;

        const baseUrl = GUDHUB_SETTINGS.conversation_server_url;

        container.innerHTML = this.pendingAttachments.map(a => `
            <div class="attachment_card${a.status === 'uploading' ? ' attachment_card--loading' : a.status === 'error' ? ' attachment_card--error' : ''}" data-id="${a.id}">
                <div class="attachment_card__preview">
                    ${a.localThumbnailUrl || a.serverFile?.thumbnail_url
                        ? `<img src="${a.localThumbnailUrl || (baseUrl + a.serverFile.thumbnail_url)}" alt="">`
                    : a.status === 'uploading'
                        ? `<span class="attachment_card__spinner"></span>`
                        : `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="22" height="22"><path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm4 18H6V4h7v5h5v11z"/></svg>`
                    }
                </div>
                <div class="attachment_card__info">
                    <span class="attachment_card__name">${a.file.name}</span>
                    <span class="attachment_card__status">${
                        a.status === 'uploading' ? 'Uploading...' :
                        a.status === 'error'     ? 'Upload failed' :
                        a.serverFile            ? this.formatFileSize(a.serverFile.size) : ''
                    }</span>
                </div>
                <button class="attachment_card__remove" data-id="${a.id}" type="button">×</button>
            </div>
        `).join('');

        container.querySelectorAll('.attachment_card__remove').forEach(btn => {
            btn.onclick = () => {
                this.pendingAttachments = this.pendingAttachments.filter(a => a.id !== btn.dataset.id);
                this.renderAttachmentCards();
            };
        });
    },

    //********************* DELETE FILE ********************************//
    // Deletes a file from the server and removes the message from both
    // the local messages array and the DOM.
    // * messageId (string) — the message whose attachment to delete
    // * filename (string) — bare filename on the server (e.g. "uuid.jpg")
    async deleteFile(messageId, filename) {
        await fetch(`${GUDHUB_SETTINGS.conversation_server_url}/gudhub-messenger/files/delete`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                user_id: this.activeUserId,
                conversation_id: this.conversationId,
                filename
            })
        });

        const messages = this.activeMessages();
        const idx = messages.findIndex(m => m.message_id === messageId);
        if(idx !== -1) messages.splice(idx, 1);

        const msgEl = this.querySelector(`.message[data-message-id="${messageId}"]`);
        if(msgEl) msgEl.remove();
    },

    //********************* FORMAT FILE SIZE ********************************//
    // * bytes (number)
    formatFileSize(bytes) {
        if(bytes < 1024) return bytes + ' B';
        if(bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
        if(bytes < 1024 * 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
        return (bytes / (1024 * 1024 * 1024)).toFixed(1) + ' GB';
    },
};
